// When we have interaction instructions, we create an overlay to highlight the next element
// to interact with. When the toggle button is clicked in this mode we advance to the next
// interaction. Clicking anywhere else on the page (including the highlighted elements) cancels
// the interactions and overlay.

import { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { BuilderMessageState, setNumInteractionsProcessed } from "@/store/slices/builderMessageSlice";
import type { RootState } from "@/store";

const OVERLAY_Z_INDEX = "1000";

export function HighlightInteractionOverlay() {
  const builderMessageState: BuilderMessageState | undefined = useAppSelector((state: RootState) => 
    'builderMessage' in state ? (state as RootState & { builderMessage: BuilderMessageState }).builderMessage : undefined
  );
  const dispatch = useAppDispatch();
  const highlightRef = useRef<{ destroy: () => void } | null>(null);
  const buttonOverlayRef = useRef<{ destroy: () => void } | null>(null);
  const buttonRef = useRef<HTMLElement | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const clickHandlerRef = useRef<((event: PointerEvent) => void) | null>(null);

  const { interactionInstructions } = builderMessageState?.lastMessageResponse ?? {};
  const { numInteractionsProcessed } = builderMessageState ?? {};

  useEffect(() => {
    // Clean up previous highlights if they exist
    if (highlightRef.current) {
      highlightRef.current.destroy();
      highlightRef.current = null;
    }
    if (buttonOverlayRef.current) {
      buttonOverlayRef.current.destroy();
      buttonOverlayRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (clickHandlerRef.current) {
      window.removeEventListener('pointerdown', clickHandlerRef.current, true);
      clickHandlerRef.current = null;
    }
    buttonRef.current = null;

    // Check if we should show a highlight
    if (!interactionInstructions?.interactions.length) {
      return;
    }

    if (numInteractionsProcessed !== undefined && numInteractionsProcessed >= interactionInstructions.interactions.length) {
      return;
    }

    const testId = interactionInstructions.interactions[numInteractionsProcessed ?? 0].dataTestId;
    
    // Create pointerdown handler
    const handlePointerDown = (event: PointerEvent) => {
      // Check if click is within the button's bounds
      if (buttonRef.current) {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const clickX = event.clientX;
        const clickY = event.clientY;
        
        const isWithinButton = 
          clickX >= buttonRect.left &&
          clickX <= buttonRect.right &&
          clickY >= buttonRect.top &&
          clickY <= buttonRect.bottom;

        if (isWithinButton) {
          event.stopImmediatePropagation();
          event.preventDefault();
          buttonRef.current.click();
          return;
        }
      }

      // Clear the highlight for any other clicks
      dispatch(setNumInteractionsProcessed(interactionInstructions.interactions.length));
    };

    const setupHighlights = () => {
      const element = document.querySelector(`[data-testid="${testId}"]`);

      if (!element) {
        return false;
      }

      // Create new highlight
      const highlight = createHighlight(element);
      highlightRef.current = highlight;

      // Create button overlay for NutMessageToggle
      const nutToggleButton = document.querySelector('[data-nut-message-toggle]') as HTMLElement;
      if (nutToggleButton) {
        buttonRef.current = nutToggleButton;
        const buttonOverlay = createButtonOverlay(nutToggleButton);
        buttonOverlayRef.current = buttonOverlay;
      }

      // Set up pointerdown listener with capture phase to execute before all other handlers
      clickHandlerRef.current = handlePointerDown;
      window.addEventListener('pointerdown', handlePointerDown, true);

      return true;
    };

    // Try to set up highlights immediately
    if (!setupHighlights()) {
      // Element not found, set up interval to retry every 500ms
      intervalRef.current = setInterval(() => {
        if (setupHighlights()) {
          // Element found, clear interval
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      }, 500);
    }

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (clickHandlerRef.current) {
        window.removeEventListener('pointerdown', clickHandlerRef.current, true);
        clickHandlerRef.current = null;
      }
      if (highlightRef.current) {
        highlightRef.current.destroy();
        highlightRef.current = null;
      }
      if (buttonOverlayRef.current) {
        buttonOverlayRef.current.destroy();
        buttonOverlayRef.current = null;
      }
    };
  }, [interactionInstructions, numInteractionsProcessed]);

  return null;
}

interface HighlightOptions {
  borderWidth?: number;
  gap?: number;
  borderRadius?: number;
  color?: string;
}

function createHighlight(element: Element, options: HighlightOptions = {}) {
  const {
    borderWidth = 10,
    gap = 4,
    borderRadius = 6,
    color = '#facc15'
  } = options;

  const rect = element.getBoundingClientRect();
  const offset = borderWidth + gap;

  const overlay = document.createElement('div');
  overlay.id = 'highlight-interaction-overlay';
  overlay.className = 'highlight-pulse';

  Object.assign(overlay.style, {
    position: 'fixed',
    top: `${rect.top - offset}px`,
    left: `${rect.left - offset}px`,
    width: `${rect.width + offset * 2}px`,
    height: `${rect.height + offset * 2}px`,
    border: `${borderWidth}px solid ${color}`,
    borderRadius: `${borderRadius}px`,
    pointerEvents: 'none',
    zIndex: OVERLAY_Z_INDEX,
    boxSizing: 'border-box'
  });

  if (!document.getElementById('highlight-pulse-styles')) {
    const style = document.createElement('style');
    style.id = 'highlight-pulse-styles';
    style.textContent = `
      .highlight-pulse {
        animation: highlightPulse 1.5s ease-in-out infinite;
      }
      @keyframes highlightPulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.4; }
      }
    `;
    document.head.appendChild(style);
  }

  document.body.appendChild(overlay);

  const updatePosition = () => {
    const newRect = element.getBoundingClientRect();
    overlay.style.top = `${newRect.top - offset}px`;
    overlay.style.left = `${newRect.left - offset}px`;
    overlay.style.width = `${newRect.width + offset * 2}px`;
    overlay.style.height = `${newRect.height + offset * 2}px`;
  };

  // Find all scrollable ancestors
  const scrollableAncestors: (Window | Element)[] = [window];
  let parent: Element | null = element.parentElement;
  while (parent) {
    const style = window.getComputedStyle(parent);
    if (style.overflow === 'auto' || style.overflow === 'scroll' || 
        style.overflowY === 'auto' || style.overflowY === 'scroll' ||
        style.overflowX === 'auto' || style.overflowX === 'scroll') {
      scrollableAncestors.push(parent);
    }
    parent = parent.parentElement;
  }

  // Add scroll listeners to all scrollable ancestors
  scrollableAncestors.forEach(ancestor => {
    ancestor.addEventListener('scroll', updatePosition, true);
  });
  window.addEventListener('resize', updatePosition);

  return {
    element: overlay,
    destroy: () => {
      overlay.remove();
      scrollableAncestors.forEach(ancestor => {
        ancestor.removeEventListener('scroll', updatePosition, true);
      });
      window.removeEventListener('resize', updatePosition);
    }
  };
}

function createButtonOverlay(button: HTMLElement) {
  const rect = button.getBoundingClientRect();

  const overlay = document.createElement('div');
  overlay.className = 'nut-toggle-overlay nut-toggle-pulse';

  const updatePosition = () => {
    const newRect = button.getBoundingClientRect();
    overlay.style.top = `${newRect.top}px`;
    overlay.style.left = `${newRect.left}px`;
    overlay.style.width = `${newRect.width}px`;
    overlay.style.height = `${newRect.height}px`;
  };

  Object.assign(overlay.style, {
    position: 'fixed',
    top: `${rect.top}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
    zIndex: OVERLAY_Z_INDEX,
    cursor: 'pointer',
    backgroundColor: 'rgba(250, 204, 21, 0.2)',
    border: '1px solid #facc15',
    borderRadius: '6px',
    opacity: '1',
  });

  // Add pulse animation styles if not already present
  if (!document.getElementById('nut-toggle-pulse-styles')) {
    const style = document.createElement('style');
    style.id = 'nut-toggle-pulse-styles';
    style.textContent = `
      .nut-toggle-pulse {
        animation: nutTogglePulse 1.5s ease-in-out infinite;
      }
      @keyframes nutTogglePulse {
        0%, 100% { 
          background-color: rgba(250, 204, 21, 0.2);
        }
        50% { 
          background-color: rgba(250, 204, 21, 0.5);
        }
      }
    `;
    document.head.appendChild(style);
  }

  document.body.appendChild(overlay);

  // Find all scrollable ancestors
  const scrollableAncestors: (Window | Element)[] = [window];
  let parent: Element | null = button.parentElement;
  while (parent) {
    const style = window.getComputedStyle(parent);
    if (style.overflow === 'auto' || style.overflow === 'scroll' || 
        style.overflowY === 'auto' || style.overflowY === 'scroll' ||
        style.overflowX === 'auto' || style.overflowX === 'scroll') {
      scrollableAncestors.push(parent);
    }
    parent = parent.parentElement;
  }

  // Add scroll listeners to all scrollable ancestors
  scrollableAncestors.forEach(ancestor => {
    ancestor.addEventListener('scroll', updatePosition, true);
  });
  window.addEventListener('resize', updatePosition);

  return {
    element: overlay,
    destroy: () => {
      overlay.remove();
      scrollableAncestors.forEach(ancestor => {
        ancestor.removeEventListener('scroll', updatePosition, true);
      });
      window.removeEventListener('resize', updatePosition);
    }
  };
}
